/**
 * Tanjong Life · workflow 01 · the self-account question, asked of the Completeness Sentinel.
 *
 * Reached only by intent (`SELF_ACCOUNT`), scoped to `completeness-sentinel`, so the loose
 * match can fire from that agent's thread and nowhere else. No keywords of its own: every
 * phrasing of "how did you improve yourself?" belongs to the intent, none of them to this
 * agent alone.
 *
 * Nothing here is invented. Both improvements are the agent's own procedural memories, the
 * one it will not claim is the probe it holds, and the number under that refusal is the
 * dimension it scores lowest on. The shape is deliberate and repeats across all seven
 * agents: what I changed, what I will not claim, and where you can check both.
 *
 * No mutation steps. An agent that moved its own record by being asked about itself would
 * be the opposite of the argument this act makes.
 */
import type { Scenario } from '../director.js';
import { SELF_ACCOUNT } from './tanjong-intents.js';

export const tanjongChatImproveCompleteness: Scenario = {
  id: 'tanjong-chat-improve-completeness',
  orgId: 'tanjong-life',
  agentPk: 'completeness-sentinel',
  keywords: [],
  intents: [SELF_ACCOUNT],
  steps: [
    {
      do: 'say',
      text: 'Twice since I was commissioned on 1 September, and both are written down with the sweep that produced them. On 2 September I finished the first requirement-drift sweep and found that the window between a file being judged complete and an assessor opening it belongs to no activity in the workflow at all. Nobody had removed it — nobody owned it. I took it, and it is now the thing I watch rather than a gap in somebody else’s handover.',
      sources: [
        'Memory · after the first requirement-drift sweep · 2 September',
        'Probes · 4 built, 1 held',
      ],
      delay: 1100,
    },
    {
      do: 'say',
      text: 'The second one I found by being wrong on purpose. I built a measure that would tell an unanswered request from an unread reply, ran it against a month the desk already knew the answer to, and watched it call nine people unresponsive who had answered on a channel I cannot read. So the rule I recorded is not a better measure — it is that a measure which cannot tell those two apart does not run at all.',
      sources: ['Memory · derived from my own July replay · 2 September'],
      delay: 1300,
    },
    {
      do: 'insight',
      title: 'What I have not improved, and will not claim',
      body: 'Telling an unanswered request from an unread reply. I score 36 out of 100 on it. I can tell you a request went out on the third and no document is on the file on the fourteenth; I cannot tell you which of those two happened, because the reply channel is not one I read. The probe is built and it is HELD, and the reason is written into the probe rather than into a footnote under a chart. My overall evaluation is 84, up four — that four came from the two rules above, not from this.',
      sources: [
        'Evaluation · 84 overall · Unanswered request vs unread reply 36',
        'Probe · Unanswered request, or unread reply · HELD',
      ],
      delay: 1400,
    },
    {
      do: 'link',
      label: 'Open my probes — including the one I am holding',
      href: '/agents/completeness-sentinel',
      delay: 600,
    },
  ],
};
